import { useMemo, useState } from "react";
import { QrCode, Download, Copy, Check, Sparkles, Printer, FileImage, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const PRESETS = [
  { label: "Main site", url: "https://aristocratsja.com" },
  { label: "VIP Portal", url: "https://aristocratsja.com/vip" },
  { label: "Booking", url: "https://aristocratsja.com/booking" },
  { label: "Marketplace", url: "https://aristocratsja.com/marketplace" },
  { label: "Chauffeur", url: "https://aristocratsja.com/chauffeur" },
  { label: "CartBlancJA", url: "https://aristocratsja.com/cartblancja" },
];

/**
 * AdminQrTab — admin QR generator with bulletproof downloads.
 * Uses fetch + Blob + URL.createObjectURL so saves work on iOS /
 * Android (not just desktop). Live preview pane forced 1:1 with
 * explicit aspectRatio inline style (mobile-safe).
 */
export default function AdminQrTab() {
  const [url, setUrl] = useState("https://aristocratsja.com");
  const [style, setStyle] = useState("gold_on_black");
  const [logo, setLogo] = useState(true);
  const [size, setSize] = useState(800);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const buildUrl = (overrides = {}) => {
    const qs = new URLSearchParams({
      url, style, logo: logo ? "1" : "0",
      size: String(size), ...overrides,
    });
    return `${API_BASE}/api/qr?${qs.toString()}`;
  };

  const preview = useMemo(() => buildUrl(), [url, style, logo, size]); // eslint-disable-line react-hooks/exhaustive-deps
  const openInTab = useMemo(() => buildUrl({ size: "3000" }), [url, style, logo]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadFile = async (kind, overrides, filename) => {
    setDownloading(kind);
    try {
      const res = await fetch(buildUrl(overrides));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 1500);
      toast.success(`${filename} downloaded`);
    } catch {
      toast.error("Download failed — long-press the preview to save instead");
    } finally {
      setDownloading(null);
    }
  };

  const copyImageUrl = async () => {
    try {
      await navigator.clipboard.writeText(preview);
      setCopied(true);
      toast.success("QR image URL copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Couldn't copy URL"); }
  };

  const valid = /^https?:\/\//i.test(url) && url.length >= 8;

  return (
    <div data-testid="admin-qr-tab" className="space-y-6">
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1">Admin · QR Generator</p>
        <h2 className="font-heading text-2xl text-white">Branded QR Codes</h2>
        <p className="text-white/45 text-sm mt-1 max-w-2xl">
          Generate luxury gold-on-black or print-friendly QR codes for any URL. Embed the AJA brand mark in the center. Export sharp PNG up to 4000 px (shirt-print at 300 DPI) or infinite-scale SVG vector for screen-print and vinyl.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CONTROLS */}
        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gold mb-1.5 block">Destination URL</label>
            <input
              data-testid="admin-qr-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
              className="w-full bg-black/40 border border-white/15 rounded px-3 py-2.5 text-sm text-white focus:border-gold/60 focus:outline-none font-mono"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-gold mb-1.5 block">Presets</label>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.url}
                  data-testid={`admin-qr-preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setUrl(p.url)}
                  className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
                    url === p.url
                      ? "border-gold/60 bg-gold/15 text-gold"
                      : "border-white/15 text-white/55 hover:text-white"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-gold mb-1.5 block">Style</label>
            <div className="flex gap-1.5">
              <button
                data-testid="admin-qr-style-gold"
                onClick={() => setStyle("gold_on_black")}
                className={`text-[10px] uppercase tracking-wider px-3 py-2 rounded-full border transition-colors ${
                  style === "gold_on_black"
                    ? "border-gold bg-gold/15 text-gold"
                    : "border-white/15 text-white/55 hover:text-white"
                }`}>Gold on black</button>
              <button
                data-testid="admin-qr-style-print"
                onClick={() => setStyle("black_on_white")}
                className={`text-[10px] uppercase tracking-wider px-3 py-2 rounded-full border transition-colors ${
                  style === "black_on_white"
                    ? "border-white bg-white/10 text-white"
                    : "border-white/15 text-white/55 hover:text-white"
                }`}>Print friendly</button>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-gold mb-1.5 block">
              Preview size · {size}px
            </label>
            <input
              type="range"
              data-testid="admin-qr-size"
              min={300} max={4000} step={100}
              value={size}
              onChange={(e) => setSize(Number(e.target.value))}
              className="w-full accent-gold-500"
            />
            <div className="flex items-center justify-between text-[9px] text-white/35 mt-1">
              <span>web · 300</span>
              <span>print · 1500</span>
              <span>shirt · 3000</span>
              <span>billboard · 4000</span>
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs text-white/65 cursor-pointer">
            <input
              type="checkbox"
              data-testid="admin-qr-logo"
              checked={logo}
              onChange={(e) => setLogo(e.target.checked)}
              className="accent-gold-500"
            />
            <Sparkles size={12} className="text-gold" />
            Embed AJA brand mark in the center
          </label>

          {/* Download buttons — fetch+Blob for reliable mobile saves */}
          <div className="space-y-2 pt-2">
            <button
              data-testid="admin-qr-download-hd"
              type="button"
              onClick={() => downloadFile("hd", { size: "3000" }, "aristocratsja-qr-hd-3000.png")}
              disabled={!valid || downloading !== null}
              className={`w-full inline-flex items-center justify-center gap-2 py-3 text-xs uppercase tracking-wider rounded font-medium transition-colors ${
                valid
                  ? "bg-gold text-luxury-black hover:bg-gold/90 disabled:opacity-60"
                  : "bg-white/10 text-white/40 cursor-not-allowed"
              }`}>
              {downloading === "hd"
                ? <><Loader2 size={14} className="animate-spin" /> Preparing HD…</>
                : <><Printer size={14} /> Download HD for print (3000 px)</>}
            </button>
            <div className="grid grid-cols-3 gap-2">
              <button
                data-testid="admin-qr-download-svg"
                type="button"
                onClick={() => downloadFile("svg", { format: "svg" }, "aristocratsja-qr.svg")}
                disabled={!valid || downloading !== null}
                title="Infinite-resolution vector — screen-print / vinyl"
                className={`inline-flex items-center justify-center gap-1 py-2.5 border text-[10px] uppercase tracking-wider rounded transition-colors ${
                  valid
                    ? "border-gold/40 text-gold hover:bg-gold/10 disabled:opacity-60"
                    : "border-white/10 text-white/40 cursor-not-allowed"
                }`}>
                {downloading === "svg"
                  ? <Loader2 size={11} className="animate-spin" />
                  : <FileImage size={11} />} SVG vector
              </button>
              <button
                data-testid="admin-qr-download-max"
                type="button"
                onClick={() => downloadFile("max", { size: "4000" }, "aristocratsja-qr-4k.png")}
                disabled={!valid || downloading !== null}
                title="Maximum 4000 px PNG"
                className={`inline-flex items-center justify-center gap-1 py-2.5 border text-[10px] uppercase tracking-wider rounded transition-colors ${
                  valid
                    ? "border-white/15 text-white/80 hover:text-white disabled:opacity-60"
                    : "border-white/10 text-white/40 cursor-not-allowed"
                }`}>
                {downloading === "max"
                  ? <Loader2 size={11} className="animate-spin" />
                  : <Download size={11} />} 4K PNG
              </button>
              <button
                data-testid="admin-qr-copy-url"
                onClick={copyImageUrl}
                disabled={!valid}
                className="inline-flex items-center justify-center gap-1 py-2.5 border border-white/15 text-white/80 hover:text-white text-[10px] uppercase tracking-wider rounded transition-colors disabled:opacity-40">
                {copied ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                {copied ? "Copied" : "Copy URL"}
              </button>
            </div>
            <button
              type="button"
              onClick={() => downloadFile("preview", {}, `aristocratsja-qr-${size}.png`)}
              disabled={!valid || downloading !== null}
              data-testid="admin-qr-download"
              className="block text-center text-[10px] uppercase tracking-wider text-white/45 hover:text-gold transition-colors pt-1 w-full disabled:opacity-40">
              {downloading === "preview"
                ? "Preparing…"
                : `Download current preview size (${size} px)`}
            </button>
            <a
              href={valid ? openInTab : "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="text-center text-[10px] uppercase tracking-wider text-white/35 hover:text-gold transition-colors inline-flex items-center justify-center gap-1 w-full pt-1">
              <ExternalLink size={10} /> Open HD in new tab
            </a>
          </div>
        </div>

        {/* PREVIEW */}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gold mb-2">Live preview</p>
          <div className={`rounded-md overflow-hidden border mx-auto ${
            style === "gold_on_black"
              ? "border-gold/40 bg-black"
              : "border-white/15 bg-white"
          }`} style={{ width: "100%", aspectRatio: "1 / 1" }}>
            {valid ? (
              <img
                src={preview}
                alt="QR preview"
                data-testid="admin-qr-preview-image"
                className="w-full h-full"
                loading="lazy"
                style={{ imageRendering: "pixelated", objectFit: "contain" }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/30 text-sm">
                <QrCode size={36} className="opacity-30" />
              </div>
            )}
          </div>
          <p className="text-[10px] text-white/40 mt-3 text-center font-body leading-relaxed">
            {valid
              ? <>Scan with your camera to test before printing.<br />
                  <span className="text-gold">SVG vector</span> is the safest pick for t-shirts &amp; large signage.</>
              : "Enter a valid https:// URL"}
          </p>
        </div>
      </div>
    </div>
  );
}
